import type { ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import styles from "@/styles/components/BreadcrumbBar.module.css"

/**
 * The trail row at the top of a page, with the page's own action on the right.
 *
 * No mark and no bar: `AppShell` carries both, and this used to be the whole
 * header only because there was no shell. It is now a row of content, which is
 * why it scrolls with the page rather than sticking to the top.
 */

export interface Crumb {
  label: string
  /** Omit on the last crumb — where you already are is not a link. */
  to?: string
}

interface BreadcrumbBarProps {
  trail: readonly Crumb[]
  action?: ReactNode
}

export default function BreadcrumbBar({ trail, action }: BreadcrumbBarProps) {
  const navigate = useNavigate()

  return (
    <div className={styles.row}>
      <nav className={styles.trail} aria-label="Breadcrumb">
        {trail.map((crumb, index) => (
          <span className={styles.crumb} key={`${crumb.label}-${index}`}>
            {index > 0 ? (
              <span className={styles.slash} aria-hidden="true">
                /
              </span>
            ) : null}
            {crumb.to ? (
              <button type="button" className={styles.link} onClick={() => navigate(crumb.to!)}>
                {crumb.label}
              </button>
            ) : (
              <span className={styles.here} aria-current="page">
                {crumb.label}
              </span>
            )}
          </span>
        ))}
      </nav>

      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  )
}
